import { useCallback, useContext } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiEvent } from '@wix/da-bi/pkg/events';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { pushGlobalConfirmationModal } from '../../Modals/redux/actionCreators';
import {
  OnUnpublishCompleteAction,
  unpublishDeviation,
} from '../../redux/deviations/actionCreators';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

// We have to get unorthodox here since the project structure between shared-react and submit-shared. We can't import
// any of studio's batch actions here, so the flow in this file is designed with that constraint in mind.

interface BaseUseUnpublishFlowParams {
  onCompleteAction: OnUnpublishCompleteAction;
  deviation?: PapiDeviation;
  onClose?: () => void;
  onConfirmOverride?: () => void;
  confirmBtnBiData?: BiEvent;
  /**
   * Since {@link onConfirmOverride} could be processing multiple deviations (but may not be -- lets keep our options open),
   * we need this plural flag to indicate to the modal which version of text to use. We'd pass through the count, but
   * that'd complicate the global confirmation modal API, so keeping this local until a wider need is established
   */
  isPlural?: boolean;
}

interface UseUnpublishFlowParamsWithDeviation extends BaseUseUnpublishFlowParams {
  /**
   * Passing this will start the singular deviation unpublish flow.
   */
  deviation: PapiDeviation;
  onConfirmOverride?: undefined;
}

interface UseUnpublishFlowParamsWithOverride extends BaseUseUnpublishFlowParams {
  deviation?: undefined;
  /**
   * To be used within studio to send through the related batch unpublish action. This cannot be used with the
   * {@link deviation} param.
   */
  onConfirmOverride: () => void;
}

type UseUnpublishFlowParams =
  | UseUnpublishFlowParamsWithDeviation
  | UseUnpublishFlowParamsWithOverride;

export function useUnpublishFlow({
  deviation,
  onClose,
  onConfirmOverride,
  onCompleteAction,
  confirmBtnBiData,
  isPlural = false,
}: UseUnpublishFlowParams) {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatchPushGlobalConfirmationModal = useAction(pushGlobalConfirmationModal);
  const dispatchUnpublishDeviation = useAction(unpublishDeviation);

  return useCallback(() => {
    dispatchPushGlobalConfirmationModal({
      // Why not rely on the `count` param of translations to handle this pluralization? The props for confirmation modals
      // as-is do not support passing it through. Rather than complicate that API further, I've opted to rely on the already
      // required isPlural param.
      titleTransKey: isPlural
        ? 'unpublish.confirmModal.title.plural'
        : 'unpublish.confirmModal.title',
      isFullscreen: false,
      icon: !isMobile && (
        <img alt="" src="https://st.deviantart.net/eclipse/illustrations/il6-1-3_140px.png" />
      ),
      variant: 'horizontal',
      textComponent: () =>
        t(isPlural ? 'unpublish.confirmModal.text.plural' : 'unpublish.confirmModal.text', {
          postProcess: 'reactPostprocessor',
          learnMoreLink: (
            <a
              className="text-link-level-2-primary underline"
              href={t('unpublish.confirmModal.text.learnMoreLink.url')}
            >
              {t('unpublish.confirmModal.text.learnMoreLink.text')}
            </a>
          ),
        }),
      confirmBtnLabelTransKey: 'unpublish.confirmModal.confirmButton.label',
      cancelBtnLabelTransKey: 'unpublish.confirmModal.cancelButton.label',
      confirmBtnBiData,
      onConfirm: () =>
        onConfirmOverride
          ? onConfirmOverride()
          : dispatchUnpublishDeviation({ deviation, onCompleteAction }),
      onRequestClose: onClose,
    });
  }, [
    t,
    deviation,
    dispatchPushGlobalConfirmationModal,
    dispatchUnpublishDeviation,
    onCompleteAction,
    onClose,
    onConfirmOverride,
    confirmBtnBiData,
    isPlural,
    isMobile,
  ]);
}
